import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface contrastState {
	backgroundColor: string;
	foregroundColor: string;
	chartPalette: string[];
	accentColor: string;
}

// Dictionary of color palettes for charts.
const contrastPalettes: Record<string, string[]> = {
	contrast: [
		"#171717",
		"#999999",
		"#404040",
		"#262626",
		"#d4d4d4",
		"#333333",
		"#b3b3b3",
		"#808080",
		"#666666",
		"#525252",
	],
};

export function getCSSVariableValue(variableName: string): string {
	return getComputedStyle(document.body).getPropertyValue(variableName).trim();
}

// Get a color array using the hex value from the --ccbg variable as the key of the contrast palettes dict.
// Return an undefined value when no contrast is applied to use the default colors (either default echarts colors or colors we apply).
export function getChartColorPalette(): string[] {
	const bgValue = getBackgroundColor();
	if (!isContrastMode() || bgValue === "#000000" || bgValue === "#000078") {
		// Use default
		return contrastPalettes["default"];
	}

	return contrastPalettes["contrast"];
}

// Get the background color using the hex value from the --ccbg variable.
// Default to #fff when no contrast is applied.
export function getBackgroundColor(): string {
	const backgroundValue = getCSSVariableValue("--ccbg");
	if (!backgroundValue) {
		return "#fff";
	}

	return backgroundValue;
}

// Get the foreground color using the hex value from the --ccfg variable.
// Default to #333 when no contrast is applied.
export function getForegroundColor(): string {
	const foregroundValue = getCSSVariableValue("--ccfg");
	if (!foregroundValue) {
		return "#333";
	}

	return foregroundValue;
}

// Get the accent color using the hex value from the --ccgf variable.
// Default to #e1e6f1 when no contrast is applied. This seems to be the echarts default accent line colors.
export function getAccentColor(): string {
	const accentValue = getCSSVariableValue("--ccfg");
	if (!accentValue) {
		return "#e1e6f1";
	}

	return accentValue;
}

export function isContrastMode(): boolean {
	return document.body.classList.contains("contrast");
}

const initialState: contrastState = {
	foregroundColor: getForegroundColor(),
	chartPalette: getChartColorPalette(),
	accentColor: getAccentColor(),
	backgroundColor: getBackgroundColor(),
};

const contrastSlice = createSlice({
	name: "contrast",
	initialState,
	reducers: {
		setBackgroundColor(state, action: PayloadAction<string>) {
			state.backgroundColor = action.payload;
		},
		setForegroundColor(state, action: PayloadAction<string>) {
			state.foregroundColor = action.payload;
		},
		setChartPalette(state, action: PayloadAction<string[]>) {
			state.chartPalette = action.payload;
		},
		setAccentColor(state, action: PayloadAction<string>) {
			state.accentColor = action.payload;
		},
	},
});

export const { setBackgroundColor, setForegroundColor, setChartPalette, setAccentColor } =
	contrastSlice.actions;
export default contrastSlice.reducer;
